import React, { useState } from "react";

interface DocsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type TabType = "pipeline" | "requisitos" | "costos" | "tips" | "minutas";

export function DocsModal({ isOpen, onClose }: DocsModalProps) {
  const [activeTab, setActiveTab] = useState<TabType>("pipeline");

  if (!isOpen) return null;

  const tabs: { id: TabType; label: string }[] = [
    { id: "pipeline", label: "Pipeline" },
    { id: "requisitos", label: "Requisitos" },
    { id: "costos", label: "Costos & Créditos" },
    { id: "tips", label: "Tips de uso" },
    { id: "minutas", label: "Minutas y Correos" },
  ];

  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-md z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div 
        className="bg-[var(--window-bg)] border border-[var(--border)] rounded-xl max-w-3xl w-full max-h-[85vh] shadow-2xl flex flex-col overflow-hidden animate-[modal-appear_0.22s_cubic-bezier(0.16,1,0.3,1)] text-[var(--text)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 bg-[var(--surface)] border-b border-[var(--border)] flex-shrink-0">
          <div className="flex items-center gap-2">
            <span className="text-lg">📖</span>
            <h2 className="text-sm font-semibold text-[var(--text)] uppercase tracking-wider font-mono">
              Documentación Técnica
            </h2>
          </div>
          <button 
            onClick={onClose}
            className="w-6 h-6 rounded-md bg-[var(--surface)] hover:bg-[var(--surface-hover)] border border-[var(--border)] text-[var(--text-muted)] hover:text-[var(--text)] flex items-center justify-center transition-all text-xs"
          >
            ✕
          </button>
        </div>

        {/* Tab Buttons */}
        <div className="flex border-b border-[var(--border)] bg-[var(--surface)] px-5 gap-2 overflow-x-auto scrollbar-none flex-shrink-0">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-3 px-3 text-xs font-medium border-b-2 transition-all whitespace-nowrap outline-none ${
                activeTab === tab.id
                  ? "border-[var(--accent)] text-[var(--accent)] font-semibold"
                  : "border-transparent text-[var(--text-dim)] hover:text-[var(--text-muted)]"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Modal Body Content */}
        <div className="p-6 overflow-y-auto flex-1 text-sm text-[var(--text-muted)] leading-relaxed font-sans scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent">
          {/* TAB: PIPELINE */}
          {activeTab === "pipeline" && (
            <div className="flex flex-col gap-6">
              <div>
                <h3 className="text-[var(--text)] font-semibold text-base mb-2">Cómo funciona internamente</h3>
                <p className="text-[var(--text-muted)] text-xs">
                  El traductor combina tres tecnologías encadenadas en tiempo real: captura de audio del sistema operativo, transcripción con IA de Deepgram vía WebSocket, y traducción automática con Google Translate. Todo sucede en el browser, sin necesidad de un proxy de audio externo.
                </p>
              </div>

              {/* Pipeline Diagram */}
              <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-center bg-[var(--surface)] border border-[var(--border)] p-4 rounded-xl text-center">
                <div className="flex flex-col items-center gap-1.5 p-2 bg-[var(--surface)] rounded-lg border border-[var(--border)]">
                  <span className="text-2xl">🎙️</span>
                  <div className="text-[11px] font-bold text-[var(--text)]">Captura</div>
                  <div className="text-[9px] text-[var(--text-dim)] leading-normal">
                    Audio del sistema capturado con getDisplayMedia()
                  </div>
                </div>
                <div className="hidden md:block text-[var(--text-dim)] text-lg">➔</div>
                <div className="flex flex-col items-center gap-1.5 p-2 bg-[var(--surface)] rounded-lg border border-[var(--border)]">
                  <span className="text-2xl">🔌</span>
                  <div className="text-[11px] font-bold text-[var(--text)]">WebSocket</div>
                  <div className="text-[9px] text-[var(--text-dim)] leading-normal">
                    Chunks cada 250ms directos a Deepgram
                  </div>
                </div>
                <div className="hidden md:block text-[var(--text-dim)] text-lg">➔</div>
                <div className="flex flex-col items-center gap-1.5 p-2 bg-[var(--surface)] rounded-lg border border-[var(--border)]">
                  <span className="text-2xl">🧠</span>
                  <div className="text-[11px] font-bold text-[var(--text)]">Nova-2 STT</div>
                  <div className="text-[9px] text-[var(--text-dim)] leading-normal">
                    Deepgram transcribe con smart_format activado
                  </div>
                </div>
              </div>

              {/* Pipeline Details */}
              <div className="flex flex-col gap-2">
                <h3 className="text-[var(--text-dim)] font-semibold text-xs uppercase tracking-wider font-mono mb-1">
                  Detalles del flujo
                </h3>
                <div className="flex justify-between py-1.5 border-b border-[var(--border)] text-xs">
                  <span className="text-[var(--text-dim)]">Protocolo de audio</span>
                  <span className="text-[var(--text)] font-medium">WebSocket (wss://) con token header</span>
                </div>
                <div className="flex justify-between py-1.5 border-b border-[var(--border)] text-xs">
                  <span className="text-[var(--text-dim)]">Fragmentación</span>
                  <span className="text-[var(--text)] font-medium">Chunks de 250 ms — latencia ultra baja</span>
                </div>
                <div className="flex justify-between py-1.5 border-b border-[var(--border)] text-xs">
                  <span className="text-[var(--text-dim)]">Mensajes recibidos</span>
                  <span className="text-[var(--text)] font-medium">Solo se procesan los de tipo is_final = true</span>
                </div>
                <div className="flex justify-between py-1.5 border-b border-[var(--border)] text-xs">
                  <span className="text-[var(--text-dim)]">Traducción</span>
                  <span className="text-[var(--text)] font-medium">Google Translate API pública (en tiempo real)</span>
                </div>
                <div className="flex justify-between py-1.5 border-b border-[var(--border)] text-xs">
                  <span className="text-[var(--text-dim)]">Document PiP</span>
                  <span className="text-[var(--text)] font-medium">Clonación de DOM a ventana flotante siempre visible</span>
                </div>
              </div>
            </div>
          )}

          {/* TAB: REQUISITOS */}
          {activeTab === "requisitos" && (
            <div className="flex flex-col gap-5">
              <div>
                <h3 className="text-[var(--text)] font-semibold text-base mb-2">Requisitos del sistema</h3>
                <p className="text-[var(--text-muted)] text-xs">
                  El traductor corre de forma autónoma en el navegador, sin requerir instalaciones pesadas.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-[var(--surface)] border border-[var(--border)] rounded-lg">
                  <div className="text-xs font-bold text-[var(--text)] mb-1">Navegador Compatible</div>
                  <div className="text-[11px] text-[var(--text-muted)]">
                    Chrome 116+ o Edge 116+. Obligatorio para soportar la captura combinada de audio y el Picture-in-Picture avanzado.
                  </div>
                </div>
                <div className="p-4 bg-[var(--surface)] border border-[var(--border)] rounded-lg">
                  <div className="text-xs font-bold text-[var(--text)] mb-1">Audio del Sistema</div>
                  <div className="text-[11px] text-[var(--text-muted)]">
                    Al hacer clic en Start, selecciona "Compartir audio del sistema" al elegir la pestaña o pantalla que emite el audio.
                  </div>
                </div>
                <div className="p-4 bg-[var(--surface)] border border-[var(--border)] rounded-lg">
                  <div className="text-xs font-bold text-[var(--text)] mb-1">Conexión de Red</div>
                  <div className="text-[11px] text-[var(--text-muted)]">
                    Conexión estable. La conexión a Deepgram por WebSocket requiere mantener un flujo continuo de subida.
                  </div>
                </div>
                <div className="p-4 bg-[var(--surface)] border border-[var(--border)] rounded-lg">
                  <div className="text-xs font-bold text-[var(--text)] mb-1">Deepgram API Key</div>
                  <div className="text-[11px] text-[var(--text-muted)]">
                    Configurada en el panel de engranaje (guardada localmente de forma segura en tu navegador).
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* TAB: COSTOS */}
          {activeTab === "costos" && (
            <div className="flex flex-col gap-5">
              <div>
                <h3 className="text-[var(--text)] font-semibold text-base mb-2">Créditos de Deepgram</h3>
                <p className="text-[var(--text-muted)] text-xs">
                  Deepgram ofrece planes de prueba muy accesibles y un costo de operación extremadamente bajo comparado con otros motores de transcripción.
                </p>
              </div>

              <div className="p-4 bg-[var(--accent-muted)] border border-[var(--accent)]/20 text-[var(--accent)] rounded-lg text-xs leading-normal">
                🎁 Al crear una cuenta en Deepgram, se otorgan <strong>$200 USD en créditos gratuitos</strong> de un solo uso sin necesidad de tarjeta. Estos créditos permiten probar la API de inmediato.
              </div>

              <h4 className="text-xs font-bold uppercase tracking-wider font-mono text-[var(--text-dim)] mt-2">
                Duración Estimada de los Créditos
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-[var(--surface)] border border-[var(--accent)]/30 rounded-lg relative overflow-hidden">
                  <div className="absolute top-2 right-2 bg-[var(--accent-muted)] text-[var(--accent)] text-[8px] font-bold uppercase px-1.5 py-0.5 rounded">
                    Nova-2 (Usado aquí)
                  </div>
                  <div className="text-xs font-semibold text-[var(--text-muted)]">Tiempo Real</div>
                  <div className="text-2xl font-bold text-[var(--text)] my-1">~566 horas</div>
                  <div className="text-[10px] text-[var(--text-dim)] leading-relaxed">
                    Tarifa: $0.0058 / minuto. Los $200 equivalen a 34,400 minutos de audio en vivo. Suficiente para más de un año con 1 hora diaria.
                  </div>
                </div>

                <div className="p-4 bg-[var(--surface)] border border-[var(--border)] rounded-lg">
                  <div className="text-xs font-semibold text-[var(--text-muted)]">Nova-3 (Modelo nuevo)</div>
                  <div className="text-2xl font-bold text-[var(--text)]/60 my-1">~432 horas</div>
                  <div className="text-[10px] text-[var(--text-dim)] leading-relaxed">
                    Tarifa: $0.0077 / minuto. Equivale a 25,900 minutos.
                  </div>
                </div>
              </div>

              <p className="text-[10px] text-[var(--text-dim)] italic">
                * Precios de referencia de Deepgram. La facturación es al segundo y no por minuto redondeado, optimizando aún más el consumo de saldo.
              </p>
            </div>
          )}

          {/* TAB: TIPS */}
          {activeTab === "tips" && (
            <div className="flex flex-col gap-4">
              <h3 className="text-[var(--text)] font-semibold text-base mb-2">Tips para obtener el mejor resultado</h3>
              <ul className="list-disc list-inside flex flex-col gap-2.5 text-xs text-[var(--text-muted)]">
                <li>
                  <strong className="text-[var(--text)] font-semibold">Compartir audio de pestaña:</strong> Para audio de YouTube, Zoom Web o Meet Web, comparte específicamente la pestaña del navegador para una calidad digital directa.
                </li>
                <li>
                  <strong className="text-[var(--text)] font-semibold">Uso de auriculares:</strong> Si hablas por el micrófono, usa auriculares para evitar que el altavoz del sistema se filtre al micrófono y cause eco o duplicación de palabras.
                </li>
                <li>
                  <strong className="text-[var(--text)] font-semibold">Configuración de Idiomas:</strong> Usa "Auto-detectar" si hay varios idiomas en la reunión, o selecciona directamente el idioma específico para mayor precisión en la transcripción.
                </li>
                <li>
                  <strong className="text-[var(--text)] font-semibold">Mantener ventana activa:</strong> No minimices la ventana flotante PiP si estás en otra aplicación, para que el navegador mantenga el motor JS con prioridad de procesamiento de audio estable.
                </li>
              </ul>
            </div>
          )}

          {/* TAB: MINUTAS */}
          {activeTab === "minutas" && (
            <div className="flex flex-col gap-4">
              <h3 className="text-[var(--text)] font-semibold text-base mb-1">Minutas automáticas con IA</h3>
              <p className="text-[var(--text-muted)] text-xs">
                Kira te permite estructurar minutas de reunión automáticamente enviando el historial completo a un modelo LLM.
              </p>

              <div className="flex flex-col gap-2.5 text-xs text-[var(--text-muted)]">
                <div className="flex gap-2">
                  <span className="text-[var(--accent)] font-bold">1.</span>
                  <div>
                    <strong className="text-[var(--text)] font-semibold">Generación Inteligente:</strong> Al presionar "Minuta" tras detener una sesión, se envía el transcript estructurado cronológicamente.
                  </div>
                </div>
                <div className="flex gap-2">
                  <span className="text-[var(--accent)] font-bold">2.</span>
                  <div>
                    <strong className="text-[var(--text)] font-semibold">Formateado de Acuerdos:</strong> El reporte clasifica los temas discutidos, las decisiones clave, los pendientes y responsables.
                  </div>
                </div>
                <div className="flex gap-2">
                  <span className="text-[var(--accent)] font-bold">3.</span>
                  <div>
                    <strong className="text-[var(--text)] font-semibold">Exportación:</strong> Puedes descargar la minuta en formato plano Markdown (.md) o en un archivo interactivo HTML (.html) estructurado al estilo macOS.
                  </div>
                </div>
                <div className="flex gap-2">
                  <span className="text-[var(--accent)] font-bold">4.</span>
                  <div>
                    <strong className="text-[var(--text)] font-semibold">Envío por Correo:</strong> Permite ingresar múltiples destinatarios separados por comas para enviarles una copia del reporte.
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
